import { yupResolver } from "@hookform/resolvers/yup";
import FormButtons, { FormButtonInterface } from "components/forms/buttons";
import { CancelButton, SubmitButton } from "components/forms/buttons.utils";
import Form from "components/forms/form";
import Dropdown from "components/forms/inputs/dropdown";
import { OptionInterface } from "components/forms/inputs/dropdown.styles";
import Col from "components/grid/col";
import Grid from "components/grid/grid";
import Row from "components/grid/row";
import Line from "components/layout/line";
import { FormSubmitFunction } from "lib/form";
import { createEmpty, repair } from "lib/locale";
import { ListEmailTemplate } from "models/list-email-template";
import { LocaleString } from "models/locale";
import { Fragment, FunctionComponent, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocale, useLocales } from "stores/hooks/locale";
import * as yup from "yup";
import ImportEmailTemplateModal from "components/import-email-template-modal";
import Datepicker from "components/forms/inputs/datepicker";
import dayjs from "dayjs";
import FormEmailRows from "components/forms/form-email-block";
import { faPaperPlane } from "components/icon-library";
import Info from "components/info";
import { RecipientTypes } from "models/list-schedule";
import { useTranslate } from "stores/hooks/translation";

interface AddScheduleModalFormProps {
  emailTemplates: ListEmailTemplate[];
  onSubmit: FormSubmitFunction;
  onClose: () => Promise<void>;
}

const AddScheduleModalForm: FunctionComponent<AddScheduleModalFormProps> = ({
  emailTemplates,
  onSubmit,
  onClose,
}) => {
  const translate = useTranslate();
  const [imp, toggleImport] = useState(false);
  const locale = useLocale();
  const locales = useLocales();

  const schema = yup
    .object({
      send_at: yup.date(),
      recipients: yup.string().validateRequired(),
      subject: yup.object().validateLocaleString(),
      message: yup.object().validateLocaleString(),
    })
    .required();

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      send_at: undefined,
      recipients: "all",
      subject: createEmpty("", locales),
      message: createEmpty("", locales),
    },
  });

  const buttons: FormButtonInterface[] = [
    { ...CancelButton, onClick: onClose },
    {
      ...SubmitButton,
      text: methods.getValues().send_at === undefined ? "Lähetä" : "Tallenna",
      postIcon: faPaperPlane,
    },
  ];

  const recipientOptions: OptionInterface[] = RecipientTypes.map((type) => {
    return { value: type, text: translate(`email_filter_${type}`) };
  });

  const handleImport = async (subject: LocaleString, message: LocaleString) => {
    // repair
    repair(subject, locale, locales);
    repair(message, locale, locales);

    // overwrite values
    methods.setValue("subject", subject);
    methods.setValue("message", message);

    // force validation
    methods.trigger();
  };

  return (
    <Fragment>
      <Form methods={methods} onSubmit={onSubmit}>
        <Grid>
          <Row>
            <Col>
              <Datepicker
                name="send_at"
                label="Lähetysaika"
                time
                clearable
                min={dayjs().toDate()}
                placeholder="Viesti lähtee heti"
              />
            </Col>
            <Col>
              <Dropdown
                name="recipients"
                label="Vastaanottajat"
                options={recipientOptions}
              />
            </Col>
          </Row>
          {methods.getValues().send_at !== undefined && (
            <Row>
              <Col>
                <Info>
                  Lopulliset viestin vastaanottajat määräytyvät luettelon
                  tilanteen mukaan viestin lähetyshetkellä.
                </Info>
              </Col>
            </Row>
          )}
        </Grid>
        <Line />
        <Grid>
          <FormEmailRows allowImport />
        </Grid>
        <FormButtons buttons={buttons} />
      </Form>
      {imp && (
        <ImportEmailTemplateModal
          emailTemplates={emailTemplates}
          onImport={handleImport}
          onClose={async () => toggleImport(false)}
        />
      )}
    </Fragment>
  );
};

export default AddScheduleModalForm;
